import { useState } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MagnifyingGlass, Package, Truck, ArrowsLeftRight, Receipt, ArrowCounterClockwise } from '@phosphor-icons/react';

const ICONS = { package: Package, truck: Truck, arrows: ArrowsLeftRight, receipt: Receipt, 'return': ArrowCounterClockwise };
const STATUS_COLORS = { available: 'bg-green-100 text-green-800', sold: 'bg-orange-100 text-orange-800', in_transit: 'bg-blue-100 text-blue-800' };
const fmtDate = (d) => {
  if (!d) return '-';
  try { const dt = d.includes('T') ? new Date(d) : new Date(d + 'T00:00:00'); return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

/**
 * Serial-number lookup for Items Master (#14): type any serial and see which device
 * it belongs to, its current status/location, and its full movement timeline.
 */
export default function SerialLookup() {
  const [serial, setSerial] = useState('');
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    const s = serial.trim();
    if (!s) { toast.error('Enter a serial number'); return; }
    setOpen(true); setLoading(true); setData(null);
    try {
      const { data: result } = await api.get(`/serial-history/${encodeURIComponent(s)}`);
      setData(result);
    } catch (e) {
      toast.error(e.response?.data?.detail || `No record found for serial "${s}"`);
      setOpen(false);
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input
            value={serial}
            onChange={e => setSerial(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') search(); }}
            placeholder="Find device by serial no..."
            className="pl-9 w-[240px]"
            data-testid="item-serial-lookup-input"
          />
        </div>
        <Button type="button" variant="outline" onClick={search} data-testid="item-serial-lookup-btn">Find</Button>
      </div>

      <Dialog open={open} onOpenChange={o => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="serial-lookup-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Serial: <span className="font-mono text-[#0F4C81]">{serial}</span></DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="py-8 text-center"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
          ) : data && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-[#64748B]">Item / Device</p><p className="text-sm font-semibold" data-testid="serial-lookup-item">{data.item_name}</p></div>
                <div className="text-right"><p className="text-xs text-[#64748B]">Status</p>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${STATUS_COLORS[data.current_status] || 'bg-gray-100 text-gray-700'}`}>{(data.current_status || '').toUpperCase()}</span></div>
              </div>
              <div className="text-xs text-[#64748B]">Current Location: <strong className="text-[#0F172A]">{data.current_location || 'N/A'}</strong></div>
              <div className="relative pl-6">
                {data.timeline?.length > 0 ? data.timeline.map((event, idx) => {
                  const IconComp = ICONS[event.icon] || Package;
                  const isLast = idx === data.timeline.length - 1;
                  return (
                    <div key={idx} className="relative pb-4">
                      {!isLast && <div className="absolute left-[-17px] top-6 bottom-0 w-0.5 bg-[#E2E8F0]" />}
                      <div className="absolute left-[-21px] top-1 w-3 h-3 rounded-full border-2 border-[#0F4C81] bg-white" />
                      <div className="border rounded-lg p-3 border-gray-300 bg-gray-50 text-gray-700">
                        <div className="flex items-center gap-2 mb-1"><IconComp size={14} weight="bold" /><span className="text-xs font-semibold">{event.event}</span><span className="text-[10px] ml-auto opacity-75">{fmtDate(event.date)}</span></div>
                        {event.location && <p className="text-[11px] opacity-80">Location: {event.location}</p>}
                        <p className="text-[11px]">{event.details}</p>
                      </div>
                    </div>
                  );
                }) : <p className="text-sm text-[#64748B] py-4">No history events recorded.</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
